import { cn } from '@quadralize/utils'
import { AltArrowDown } from '@solar-icons/react'
import type { ReactNode } from 'react'
import * as React from 'react'
import { Card } from '../card/card'
import { type CustomAction, DataTableRowActions } from '../data-table/data-table-row-actions'

export interface MobileListCardMeta {
  label: string
  value: ReactNode
  icon?: ReactNode
}

interface MobileListCardActionsConfig<T> {
  item: T
  onEdit?: (item: T) => void
  isActive?: boolean
  onToggleStatus?: (item: T) => void
  customActionGroups?: CustomAction<T>[][]
}

interface MobileListCardProps<T> {
  item: T
  itemId: string
  ariaLabel: string
  className?: string
  icon?: ReactNode
  iconContainerClassName?: string
  title: string
  subtitle?: string
  metadata?: MobileListCardMeta[]
  badges?: ReactNode
  highlight?: {
    label: string
    value: string
    variant?: 'primary' | 'success' | 'error' | 'warning'
  }
  children?: ReactNode
  inactive?: boolean
  actions: MobileListCardActionsConfig<T>
}

const HIGHLIGHT_VARIANT_CLASSES: Record<string, string> = {
  primary: 'text-primary',
  success: 'text-success',
  error: 'text-error',
  warning: 'text-warning',
}

export function MobileListCard<T>({
  itemId,
  ariaLabel,
  className,
  icon,
  iconContainerClassName,
  title,
  subtitle,
  metadata,
  badges,
  highlight,
  children,
  inactive,
  actions,
}: MobileListCardProps<T>): ReactNode {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const hasCustomActionGroups =
    actions.customActionGroups?.some((group) => group.length > 0) ?? false
  const hasActions = !!actions.onEdit || !!actions.onToggleStatus || hasCustomActionGroups
  const hasMetadata = !!metadata?.length
  const isSingleMetadata = (metadata?.length ?? 0) === 1
  const hasBadges = !!badges
  const hasChildren = !!children
  const hasBody = hasMetadata || hasBadges || !!highlight || hasChildren

  return (
    <Card
      key={itemId}
      role={hasActions ? 'button' : undefined}
      tabIndex={hasActions ? 0 : undefined}
      aria-expanded={hasActions ? isExpanded : undefined}
      aria-label={ariaLabel}
      onClick={hasActions ? () => setIsExpanded((prev) => !prev) : undefined}
      onKeyDown={(e) => {
        if (!hasActions) {
          return
        }

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setIsExpanded((prev) => !prev)
        }
      }}
      className={cn(
        'overflow-hidden border-border-default/50 shadow-sm',
        'hover:bg-bg-subtle/50 transition-all duration-200 active:scale-[0.99]',
        hasActions &&
          'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
        isExpanded && 'ring-2 ring-primary/20 bg-bg-subtle/50',
        inactive && 'opacity-60 grayscale-[0.3]',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          {icon && (
            <div
              className={cn(
                'h-9 w-9 rounded-full flex items-center justify-center shrink-0',
                iconContainerClassName ?? 'bg-primary/10',
              )}
            >
              {icon}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold truncate text-text-primary leading-tight">{title}</h3>
            {subtitle && (
              <p className="mt-0.5 text-[11px] text-text-tertiary truncate leading-tight">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {highlight && (
            <span
              className={cn(
                'text-sm font-bold',
                HIGHLIGHT_VARIANT_CLASSES[highlight.variant ?? 'primary'],
              )}
            >
              {highlight.value}
            </span>
          )}
          {hasActions && (
            <AltArrowDown
              className={cn(
                'h-3.5 w-3.5 text-text-tertiary transition-transform duration-200 shrink-0',
                isExpanded && 'rotate-180',
              )}
            />
          )}
        </div>
      </div>

      {/* Body */}
      {hasBody && (
        <div className="flex flex-col gap-3 mt-3 pt-3 border-t border-border-default/40">
          {/* Metadata rows */}
          {hasMetadata && (
            <dl
              className={cn('gap-2.5', isSingleMetadata ? 'grid grid-cols-1' : 'grid grid-cols-2')}
            >
              {metadata.map((meta) => (
                <div key={meta.label} className="min-w-0">
                  {isSingleMetadata ? (
                    <div className="flex items-center justify-between gap-3 rounded-lg bg-bg-subtle/35 px-2.5 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {meta.icon && (
                          <div className="shrink-0 text-text-tertiary">{meta.icon}</div>
                        )}
                        <dt className="m-0 text-[10px] font-bold uppercase tracking-wider text-text-tertiary leading-none">
                          {meta.label}
                        </dt>
                      </div>
                      <dd className="m-0 min-w-0 max-w-[70%] truncate text-right text-xs font-semibold text-text-secondary">
                        {meta.value}
                      </dd>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1 rounded-lg bg-bg-subtle/25 px-2.5 py-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {meta.icon && (
                          <div className="shrink-0 text-text-tertiary">{meta.icon}</div>
                        )}
                        <dt className="m-0 text-[10px] font-bold uppercase tracking-wider text-text-tertiary leading-none">
                          {meta.label}
                        </dt>
                      </div>
                      <dd className="m-0 text-xs font-semibold text-text-secondary leading-tight wrap-break-word">
                        {meta.value}
                      </dd>
                    </div>
                  )}
                </div>
              ))}
            </dl>
          )}

          {/* Footer content */}
          {hasChildren && hasBadges ? (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">{children}</div>
              <div className="flex items-center gap-2 flex-wrap justify-end">{badges}</div>
            </div>
          ) : (
            <>
              {hasChildren && children}
              {hasBadges && <div className="flex items-center gap-2 flex-wrap">{badges}</div>}
            </>
          )}
        </div>
      )}

      {/* Accordion actions */}
      {hasActions && (
        <DataTableRowActions
          item={actions.item}
          isActive={actions.isActive}
          onEdit={actions.onEdit}
          onToggleStatus={actions.onToggleStatus}
          customActionGroups={actions.customActionGroups}
          mobileBehavior="accordion"
          isExpanded={isExpanded}
          onExpandedChange={setIsExpanded}
          hideTrigger
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </Card>
  )
}
